import React, { useCallback, useState } from "react";
import { Box, CircularProgress, Alert, Typography } from "@mui/material";
import { apiClient } from "../utils/http";
import { useAuth } from "../context/AuthContext";
import usePaginatedList from "../hooks/usePaginatedList";
import useDebouncedValue from "../hooks/useDebouncedValue";
import OrderCard from "../components/OrderCard";
import OrdersAppBar from "../components/OrdersAppBar";
import LoadMoreButton from "../components/LoadMoreButton";

export default function CompletedOrders() {
  const { store } = useAuth();
  const currency = store?.currency || store?.price_type || "";

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);

  // GET pos/v1/orders/get-orders?st=completed&search=...&page=N — search filters
  // by order_no, order_type, customer name, or product name server-side
  // (OrderController@getOrders). st=completed's status filter is actually
  // ['completed', 'created', 'cancelled'] server-side (see the comment in that
  // controller) — cancelled orders belong here (this is where a cancelled
  // order's final outcome is shown), 'created' drafts don't, so only that one
  // is dropped client-side.
  const fetchPage = useCallback(
    (page) =>
      apiClient.get("orders/get-orders", { params: { st: "completed", search: debouncedSearch, page } }).then((res) => {
        const paginator = res.data?.orders || {};
        const items = (paginator.data || []).filter((o) => ["completed", "cancelled"].includes(o.status));
        return { items, hasMore: (paginator.current_page || 1) < (paginator.last_page || 1) };
      }),
    [debouncedSearch]
  );
  const { items: orders, loading, loadingMore, hasMore, error, loadMore } = usePaginatedList(fetchPage, [debouncedSearch]);

  return (
    <Box>
      <OrdersAppBar title="Completed Orders" search={search} onSearchChange={setSearch} />

      <Box sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
            {debouncedSearch ? "No matching orders." : "No completed or cancelled orders yet."}
          </Typography>
        ) : (
          <>
            {orders.map((o) => (
              <OrderCard key={o.id} order={o} currency={currency} />
            ))}
            <LoadMoreButton hasMore={hasMore} loadingMore={loadingMore} onClick={loadMore} />
          </>
        )}
      </Box>
    </Box>
  );
}
